// ═══════════════════════════════════════════════════════════════
// FK-5112 MICRO-SUPPRESSION CALCULATION ENGINE v2
// Multi-enclosure support with per-enclosure + aggregated BOM
// ═══════════════════════════════════════════════════════════════
import { toMeters, toCelsius, volToM3, calculateAgentMass } from "./totalFloodingEngine";

export { toMeters };

// ─── Single enclosure volume ───
export function enclosureVolume(l, w, h) {
  const L = parseFloat(l) || 0;
  const W = parseFloat(w) || 0;
  const H = parseFloat(h) || 0;
  return Math.round(L * W * H * 100) / 100;
}

// ─── Micro cylinder selection (max 18 lb per cylinder) ───
export function selectMicroCylinder(agentLbs) {
  if (agentLbs < 1) return { error: "Agent mass below 1 lb for this enclosure." };
  let n = 1;
  if (agentLbs > 18) n = Math.ceil(agentLbs / 18);
  const perCyl = agentLbs / n;

  let sizeKey, range;
  if (perCyl >= 1 && perCyl <= 3) { sizeKey = "3"; range = "3 LB"; }
  else if (perCyl > 3 && perCyl <= 6) { sizeKey = "6"; range = "6 LB"; }
  else if (perCyl > 6 && perCyl <= 12) { sizeKey = "12"; range = "12 LB"; }
  else { sizeKey = "18"; range = "18 LB"; }

  return { sizeKey, range, perCyl: Math.round(perCyl), count: n };
}

// ─── Model mapping ───
export function mapMicroModel(sizeKey, config) {
  if (config === "Direct") return `CR K${sizeKey}D-SF`;
  if (config === "Indirect") return `CR K${sizeKey}S-SF`;
  if (config === "Pre-Engineering") return `CR K${sizeKey}-PE-SF`;
  return "—";
}

function getModelDesc(sizeKey, config) {
  const prefix = config === "Direct" ? "DIRECT" : config === "Indirect" ? "INDIRECT" : "PRE-ENG";
  return `${sizeKey} LB ${prefix} CYLINDER FK5112 W/ PRESSURE GAUGE, SS BRACKET - STANDARD FILL`;
}

// ─── Tubing calculation (meters) ───
export function calculateTubing(l, w, h, isMetric) {
  const L = toMeters(parseFloat(l) || 0, isMetric);
  const W = toMeters(parseFloat(w) || 0, isMetric);
  const H = toMeters(parseFloat(h) || 0, isMetric);
  if (L <= 0 || W <= 0 || H <= 0) return 0;
  const hPasses = Math.ceil(H / 0.4);
  const metersPerSlice = (hPasses + 1) * L + hPasses * 0.4;
  const slices = Math.ceil(W / 0.8);
  return Math.ceil(metersPerSlice * slices);
}

// ─── Micro nozzle count ───
export function calculateMicroNozzles(l, w, isMetric) {
  const L = toMeters(parseFloat(l) || 0, isMetric);
  const W = toMeters(parseFloat(w) || 0, isMetric);
  if (L <= 0 || W <= 0) return 0;
  return Math.ceil((L * W) / 0.5625);
}

// ─── Calculate single enclosure BOM ───
export function calculateEnclosureBOM(enclosure, sharedConfig, isMetric) {
  const { l, w, h, name } = enclosure;
const { elevation, minTemp, cylinderConfig, pressureSupervisor,
          detectionType, supervisableSolenoid, effectiveConc } = sharedConfig;

  const vol = enclosureVolume(l, w, h);
  if (vol <= 0) return null;

  const alt_m = toMeters(parseFloat(elevation) || 0, isMetric);
  const temp_c = toCelsius(parseFloat(minTemp) || 25, isMetric);
  const vol_m3 = volToM3(vol, isMetric);
  const agentLbs = calculateAgentMass(vol_m3, temp_c, effectiveConc, alt_m);

  const cylResult = selectMicroCylinder(agentLbs);
  if (cylResult.error) return { error: cylResult.error, name };

  const model = mapMicroModel(cylResult.sizeKey, cylinderConfig);
  const items = [];

  // 1. Cylinder
  items.push({ pn: model, desc: getModelDesc(cylResult.sizeKey, cylinderConfig), qty: cylResult.count });

  // 2. Pressure Supervisor
  if (pressureSupervisor) {
    items.push({ pn: "CR 50138-3", desc: "CYLINDER PRESSURE SUPERVISORY SWITCH (135PSI)", qty: cylResult.count });
  }

  // 3. Solenoid (Pre-Engineering + Aspirating only)
  if (cylinderConfig === "Pre-Engineering" && detectionType === "Active Aspirating") {
    if (supervisableSolenoid) {
      items.push({ pn: "CR 50026-A + CR 61033-2", desc: "LATCHING SOLENOID WITH SUPERVISION + LOCAL MANUAL CONTROL", qty: cylResult.count });
    } else {
      items.push({ pn: "CR 60010", desc: "ELECTRIC MICRO SOLENOID 11 WATTS 24 VDC W/O SUPERVISION", qty: cylResult.count });
    }
  }

  // 4. Cabinet Aspirating Detector (Pre-Eng + Aspirating)
  if (cylinderConfig === "Pre-Engineering" && detectionType === "Active Aspirating") {
    items.push({ pn: "CSD-H10", desc: "SMALL SPACES ASD: SMOKE, TEMP & HUMIDITY SENSORS", qty: 1 });
    items.push({ pn: "ASP-0102-08", desc: "8 mm EXTERNAL FILTER FOR CABINET DETECTOR", qty: 1 });
  }

  // 5. Detection tubing (meters, NOT rolls - we aggregate later)
  const hasTubing = cylinderConfig === "Direct" || cylinderConfig === "Indirect" ||
    (cylinderConfig === "Pre-Engineering" && detectionType === "Thermal Tubing");
  let tubingMeters = 0;
  if (hasTubing) {
    tubingMeters = calculateTubing(l, w, h, isMetric);
  }

  // 6. End-of-line adapter
  if (hasTubing) {
    if (cylinderConfig === "Direct") {
      items.push({ pn: "CR 99025 + CR 27-15-18-DC-3", desc: "END-OF-LINE ADAPTER W/ PRESSURE GAUGE", qty: 1 });
    } else {
      items.push({ pn: "CR 99025 + CR 99001 / CR 62033", desc: "END-OF-LINE ADAPTER W/ MANUAL RELEASE OR WALL MOUNT CONTROL", qty: 1 });
    }
  }

  // 7. Nozzles (Indirect + Pre-Eng only)
  let nozzleCount = 0;
  if (cylinderConfig === "Indirect" || cylinderConfig === "Pre-Engineering") {
    nozzleCount = calculateMicroNozzles(l, w, isMetric);
  }

  return {
    name, agentLbs, model, cylResult, items, tubingMeters, nozzleCount,
    volume: vol, cylCount: cylResult.count, fillRange: cylResult.range,
    l: enclosure.l, w: enclosure.w, h: enclosure.h,
  };
}

// ─── Generate full multi-enclosure BOM ───
export function generateMultiEnclosureBOM(enclosures, sharedConfig, isMetric) {
  const results = [];
  let totalAgent = 0;
  let totalTubingMeters = 0;
  let totalNozzles = 0;
  const aggregated = {}; // pn -> { desc, qty }

  for (const enc of enclosures) {
    if (!enc.l || !enc.w || !enc.h) continue;
    const r = calculateEnclosureBOM(enc, sharedConfig, isMetric);
    if (!r) continue;
    if (r.error) return { error: `${r.name}: ${r.error}` };

    results.push(r);
    totalAgent += r.agentLbs;
    totalTubingMeters += r.tubingMeters;
    totalNozzles += r.nozzleCount;

    // Aggregate items
    for (const item of r.items) {
      if (aggregated[item.pn]) {
        aggregated[item.pn].qty += item.qty;
      } else {
        aggregated[item.pn] = { desc: item.desc, qty: item.qty };
      }
    }
  }

  if (results.length === 0) return { error: "No valid enclosures. Enter dimensions for at least one enclosure." };

  // Add tubing rolls to aggregated (total meters / 100, rounded up)
  if (totalTubingMeters > 0) {
    const rolls = Math.ceil(totalTubingMeters / 100);
    aggregated["CR 99011-R6-ROLL"] = {
      desc: "FIRE DETECTION TUBING RED 100°C (ID:4MM, OD:6MM) - 100M ROLL",
      qty: rolls,
      highlight: true,
    };
  }

  // Add aspirating tubing roll for Pre-Eng + Aspirating
  if (sharedConfig.cylinderConfig === "Pre-Engineering" && sharedConfig.detectionType === "Active Aspirating") {
    if (!aggregated["ASP-0012"]) {
      aggregated["ASP-0012"] = { desc: "8mm CAPILLARY TUBING FOR SMOKE DETECTOR", qty: 1 };
    }
  }

  // Add nozzles
  if (totalNozzles > 0) {
    aggregated["CR 99078"] = {
      desc: "TYPE C CLEAN AGENT CONE NOZZLE - STAINLESS STEEL",
      qty: totalNozzles,
      highlight: true,
    };
  }

  // Convert aggregated to array with ordering:
  // 1. Cylinders (CR K...) first
  // 2. Everything else in the middle
  // 3. Detector components (CSD-H10, ASP-*) last
  const detectorPNs = ["CSD-H10", "ASP-0102-08", "ASP-0012"];
  const allItems = Object.entries(aggregated).map(([pn, v]) => ({
    pn, desc: v.desc, qty: v.qty,
  }));

  const cylItems = allItems.filter(i => i.pn.startsWith("CR K"));
  const detectorItems = allItems.filter(i => detectorPNs.includes(i.pn));
  const middleItems = allItems.filter(i => !i.pn.startsWith("CR K") && !detectorPNs.includes(i.pn));
  const aggregatedBom = [...cylItems, ...middleItems, ...detectorItems];

  return {
    enclosureResults: results,
    totalAgent,
    totalTubingMeters,
    totalNozzles,
    aggregatedBom,
  };
}
